import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2 as CheckCircle2Icon,
  Copy as CopyIcon,
  Trash2 as Trash2Icon,
  MoreVertical as MoreVerticalIcon,
} from 'lucide-react';
import { ConfidenceBadge, ExpertValidationBadge } from './ConfidenceRating';

/**
 * SavedValuationCard - Card component for displaying a saved valuation scenario
 * Ported from Polymet saved-valuation-card.tsx → JSX with Tailwind + succession theme
 *
 * @param {Object} props
 * @param {Object} props.valuation - Enriched valuation data
 * @param {string} props.valuation.id - Scenario ID
 * @param {string} props.valuation.name - Scenario name
 * @param {boolean} props.valuation.isActive - Whether this is the currently loaded scenario
 * @param {Object} props.valuation.author - Author info { name, type, avatar, credentials }
 * @param {Object} props.valuation.valuation - Values { value, low, high } (pre-formatted strings)
 * @param {string} props.valuation.methodology - Methodology description string
 * @param {'low'|'medium'|'high'} props.valuation.confidence - Confidence level
 * @param {Object} [props.valuation.expertValidation] - Expert validation data (for ExpertValidationBadge)
 * @param {string} props.valuation.updatedAtFormatted - Relative date string (e.g. "30 minutes ago")
 * @param {Function} props.onLoad - Called when card is clicked to load this valuation
 * @param {Function} [props.onDuplicate] - Called to duplicate this valuation
 * @param {Function} [props.onDelete] - Called to delete this valuation
 * @param {string} [props.className] - Additional CSS classes
 */
const SavedValuationCard = ({
  valuation,
  onLoad,
  onDuplicate,
  onDelete,
  className = '',
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isExpert = valuation.author?.type === 'expert';
  const isValidated = valuation.expertValidation?.isValidated || false;
  const canDelete = valuation.author?.type === 'user';

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group relative bg-succession-bg-primary border rounded-lg p-4 hover:border-succession-primary/50 transition-all cursor-pointer ${
        valuation.isActive
          ? 'border-succession-primary bg-succession-primary/5'
          : 'border-succession-border'
      } ${className}`}
      onClick={() => onLoad(valuation)}
    >
      {/* Active Indicator */}
      {valuation.isActive && (
        <div className="absolute top-3 right-3">
          <span
            className="inline-flex items-center gap-1 rounded-full text-xs font-medium text-white"
            style={{
              backgroundColor: '#059669', // emerald-600
              padding: '4px 10px',
            }}
          >
            <CheckCircle2Icon className="w-3 h-3" />
            Active
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        {/* Avatar */}
        {valuation.author?.avatar ? (
          <img
            src={valuation.author.avatar}
            alt={valuation.author.name}
            className="w-10 h-10 rounded-full border-2 border-succession-border object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-full border-2 border-succession-border bg-succession-bg-tertiary flex items-center justify-center text-succession-text-secondary font-semibold text-sm flex-shrink-0">
            {(valuation.author?.name || 'U').charAt(0).toUpperCase()}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-succession-text-primary truncate">
                {valuation.name}
              </h4>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm text-succession-text-secondary">
                  {valuation.author?.name || 'You'}
                </span>
                {isExpert && valuation.author?.credentials && (
                  <span
                    className="inline-flex items-center rounded text-xs font-medium"
                    style={{
                      backgroundColor: '#F3F4F6', // gray-100
                      color: '#374151', // gray-700
                      padding: '2px 6px',
                    }}
                  >
                    {valuation.author.credentials}
                  </span>
                )}
              </div>
            </div>

            {/* Actions Menu */}
            {!valuation.isActive && (onDuplicate || (canDelete && onDelete)) && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  className="h-8 w-8 p-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity text-succession-text-secondary hover:bg-succession-bg-tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(!menuOpen);
                  }}
                >
                  <MoreVerticalIcon className="w-4 h-4" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-40 bg-succession-bg-primary border border-succession-border rounded-lg shadow-lg overflow-hidden z-50">
                    {onDuplicate && (
                      <button
                        type="button"
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-succession-text-primary hover:bg-succession-bg-tertiary text-left"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDuplicate(valuation);
                          setMenuOpen(false);
                        }}
                      >
                        <CopyIcon className="w-4 h-4" />
                        Duplicate
                      </button>
                    )}
                    {canDelete && onDelete && (
                      <>
                        {onDuplicate && (
                          <div className="border-t border-succession-border" />
                        )}
                        <button
                          type="button"
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left"
                          style={{ color: '#DC2626' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FEF2F2';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(valuation);
                            setMenuOpen(false);
                          }}
                        >
                          <Trash2Icon className="w-4 h-4" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Valuation Info */}
      <div className="space-y-2 mb-3">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-succession-text-primary">
            {valuation.valuation?.value || '$0'}
          </span>
          <span className="text-sm text-succession-text-secondary">
            ({valuation.valuation?.low || '$0'} -{' '}
            {valuation.valuation?.high || '$0'})
          </span>
        </div>
        <div className="text-xs text-succession-text-secondary">
          {valuation.methodology || 'Unknown Method'}
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 mb-3">
        {valuation.confidence && (
          <ConfidenceBadge level={valuation.confidence} size="sm" />
        )}
        {isValidated && valuation.expertValidation && (
          <ExpertValidationBadge
            validation={valuation.expertValidation}
            size="sm"
            showDetails={false}
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-succession-text-secondary pt-2 border-t border-succession-border">
        <span>Updated {valuation.updatedAtFormatted || 'recently'}</span>
        {isExpert && (
          <span
            className="inline-flex items-center rounded border text-xs"
            style={{
              borderColor: '#E5E7EB', // gray-200
              padding: '2px 6px',
            }}
          >
            Expert Created
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default SavedValuationCard;
